import {
  Box,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  Stack,
  Typography,
  Button,
  Autocomplete,
} from "@mui/material";
import {
  StyledTextField,
} from "../../customComponents/styled/styledInputs";
import CustomFileInput from "../../customComponents/CustomFileInput";
import React from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import { useImportStudentsMutation } from "./studentApiSlice";
import { useUploadFileMutation } from "../../../app/utilsApiSlice";
import { useGetClassesPaginatedQuery } from "../classroom/classApiSlice";
import toast from "../../../helpers/toast";

const templateUrl = "/student-bulk-upload-template.xlsx";
const validationSchema = yup.object({
  url: yup.string().required("Required"),
  classroom_id: yup.number().required("Required"),
});

function AddBulkStudent() {
  const [classroomSearch, setClassroomSearch] = React.useState("");
  const { data: classroomsData } = useGetClassesPaginatedQuery({ page: 1, per_page: 10, q: classroomSearch });
  const classrooms = classroomsData?.classrooms ?? [];
  const [importStudents, { isLoading: importLoading }] =
    useImportStudentsMutation();
  const [uploadFile, { isLoading: fileLoading }] = useUploadFileMutation();

  const formik = useFormik({
    initialValues: {
      url: "",
      classroom_id: "",
    },

    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        const response = await importStudents(values).unwrap();
        if (response) {
          toast.success(response.message || "Students successfully imported");
          formik.resetForm();
        }
      } catch (error) {
        if (error.status === 500) {
          toast.error(error?.message || "Something went wrong");
        } else {
          toast.error(error?.message || "Something went wrong");
        }
      }
    },
  });

  const handleFileChange = async (e) => {
    try {
      const file = e.target.files[0];
      const formData = new FormData();
      formData.append("file", file);
      const response = await uploadFile(formData).unwrap();
      if (response) {
        formik.setFieldValue(e.target.name, response.url);
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  return (
    <StyledPaper>
      <Box className="paperHeader">
        <Typography variant="formHeading">
          Bulk Student Registration Form
        </Typography>
      </Box>
      <Box className="paperBody">
        <Box component="form" onSubmit={formik.handleSubmit}>
          <Grid container columnSpacing={1} rowSpacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl
                error={
                  formik.touched.classroom_id && formik.errors.classroom_id
                }
                fullWidth
              >
                <Typography htmlFor="classroom" variant="formSubHeading">
                  classroom
                </Typography>
                <Autocomplete
                  options={classrooms}
                  getOptionLabel={(option) => option.name || ""}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={classrooms.find((c) => c.id === formik.values.classroom_id) || null}
                  onChange={(_, value) => formik.setFieldValue("classroom_id", value?.id || "")}
                  onInputChange={(_, value, reason) => { if (reason === "input") setClassroomSearch(value); }}
                  filterOptions={(x) => x}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      placeholder="Search classroom..."
                      error={formik.touched.classroom_id && Boolean(formik.errors.classroom_id)}
                    />
                  )}
                />
                <FormHelperText variant="error" sx={{ color: "red" }}>
                  {formik.touched.classroom_id && formik.errors.classroom_id
                    ? `*${formik.errors.classroom_id}`
                    : ""}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <Typography variant="formSubHeading">file</Typography>

                <Stack direction="row" spacing={1} alignItems="center">
                  <CustomFileInput
                    id="url"
                    name="url"
                    accept=".xlsx,.xls,.csv"
                    placeholder="Choose excel or csv file..."
                    value={formik.values.url}
                    onChange={(e) => handleFileChange(e)}
                    error={formik.touched.url && Boolean(formik.errors.url)}
                  />
                  {fileLoading && <CircularProgress size={20} />}
                </Stack>
                <FormHelperText variant="error" sx={{ color: "red" }}>
                  {formik.touched.url && formik.errors.url
                    ? `*${formik.errors.url}`
                    : ""}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={12} textAlign={"right"}>
              <StyledLoadingButton
                loading={importLoading}
                disabled={importLoading || fileLoading}
                type="submit"
              >
                SUBMIT
              </StyledLoadingButton>
            </Grid>
            <Button href={templateUrl} download>
              Download Template Guide
            </Button>
          </Grid>
        </Box>
      </Box>
    </StyledPaper>
  );
}

export default AddBulkStudent;
